/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import React, {
  useState, useCallback, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useSpring, easings, animated } from 'react-spring';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import ResizeObserver from 'react-resize-observer';
import Shimmer from 'react-shimmer-effect';
import IcomoonReact from 'icomoon-react';
import Checkbox from '@material-ui/core/Checkbox';
import { ThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import Select from 'react-select';
import TBDatePicker from '../../../shared/components/TBDatePicker/TBDatePicker';
import Text from '../../../shared/components/Text/Text';
import capitalizeWords from '../../../shared/utils/capitalizeWords';
import StaticAddColumnDropdownSimple from '../components/presentational/StaticAddColumnDropdownSimple';
import SwapPropDropdown from '../components/presentational/SwapPropDropdown';
import SelectDropdownExercisePickerLocal from '../components/presentational/SelectDropdownExercisePickerLocal';
import iconSet from '../../../shared/images/teambuildr-selection.json';

import { updateFutureOccurrencesProgrammedCalendarItem, fetchFutureOccurrencesCount } from '../ducks/calendarActions';
import { showErrorToast } from '../../../shared/utils/toastManager';

// Create custom SVG icons for the checkboxes
const CustomUncheckedIcon = () => (
  <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <rect x='0.5' y='0.5' width='17' height='17' rx='1.5' stroke='#E0E0E0' />
  </svg>
);

const CustomCheckedIcon = () => (
  <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <rect x='0.5' y='0.5' width='17' height='17' rx='1.5' fill='white' stroke='#77A7FF' />
    <path d='M5 9L8 12L13 6' stroke='#77A7FF' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

// Styled Checkbox component
const StyledCheckbox = styled(Checkbox)`
  padding: 4px;

  &:hover {
    background-color: rgba(119, 167, 255, 0.04);
  }
`;

// Custom theme for the checkboxes
const checkboxTheme = createMuiTheme({
  palette: {
    primary: {
      main: '#77A7FF',
    },
    secondary: {
      main: '#E0E0E0',
    },
    background: {
      paper: 'white',
    },
  },
  overrides: {
    MuiCheckbox: {
      root: {
        '&$checked': {
          '& .MuiIconButton-label': {
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              backgroundColor: 'white',
              borderRadius: 2,
              border: '1px solid #77A7FF',
              zIndex: -1,
            },
          },
        },
      },
      checked: {},
    },
  },
});

// Styled Components
const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  overflow: ${(props) => (props.hideOverflow ? 'hidden' : 'visible')};
`;

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-x: scroll;
`;

const WrapperRow = styled('div')`
  display: flex;
  width: 100%;
  background: #FCFCFC;
  padding: 8px;
`;

const StaticDropdownWrapper = styled('div')`
  display: flex;
  margin-left: 16px;
  align-items: flex-start;
  flex-direction: column;
`;

const PrimaryRow = styled('div')`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
  margin-top: 0px;
  opacity: ${(props) => (props.disabled ? '0.5' : '1')};
  user-select: ${(props) => (props.disabled ? 'none' : 'auto')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
`;

const PrimaryButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 35px;
  background: ${(props) => (props.disabled ? '#E0E0E0' : '#444444')};
  color: ${(props) => (props.disabled ? '#9E9E9E' : 'white')};
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  user-select: none;
  border: ${(props) => (props.disabled ? '1px solid #E0E0E0' : '1px solid #444444')};
  margin-bottom: 8px;
`;

const SecondaryButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 35px;
  background: white;
  color: #444444;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  user-select: none;
  border: 1px solid #444444;
`;

const ButtonsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 24px;
`;

const ContentSection = styled('div')`
  margin-bottom: 24px;
`;

const SectionTitle = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 600;
  color: #444444;
  margin-bottom: 16px;
`;

const ExerciseNameDisplay = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 400;
  color: #444444;
  background: #F5F5F5;
  padding: 12px 16px;
  border-radius: 4px;
  border: 1px solid #E0E0E0;
`;

const DateLabel = styled('label')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: #444444;
  margin-bottom: 8px;
  display: block;
`;

const DatePickerRow = styled('div')`
  display: flex;
  gap: 16px;
  width: 100%;
`;

const DatePickerWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 50%;
`;

const SlideContent = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SetPrimaryValueContainer = styled('div')`
  flex: 0 0 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => (props.shimmer ? 'transparent' : 'inherit')};
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
`;

const ShimmerRowValue = styled('div')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  text-align: center;
  border-radius: 6px;
  color: transparent;
  margin-left: 5px;
  margin-right: 5px;
`;

const PrimaryValueContainer = styled('div')`
  flex: 1;
  min-width: 160px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => (props.shimmer ? 'transparent' : 'inherit')};
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
`;

const PrimaryRowValue = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  margin-bottom: 3px;
  color: ${(props) => (props.disabled ? '#BDBDBD' : 'inherit')};
`;

const NameAndDropdown = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3px;
  position: relative;
`;

const InfoIconWrapper = styled('div')`
  position: absolute;
  left: -20px;
  display: flex;
  align-items: center;
`;

const ValueRow = styled('div')`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const ErrorAndRowsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const RowValueContainer = styled('div')`
  flex: 1;
  min-width: 160px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
`;

const RowsContainer = styled('div')`
  height: 40px;
  display: flex;
  width: 100%;
  align-items: flex-end;
  gap: 16px;
  opacity: ${(props) => (props.disabled ? '0.5' : '1')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
`;

const SetRowValueContainer = styled('div')`
  flex: 0 0 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
`;

const SetRowValue = styled('input')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  background: #FCFCFC;
  border: 1px solid #EEEEEE;
  text-align: center;
  border-radius: 4px;
`;

const RowValue = styled('input')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  background: ${(props) => props.background};
  border: ${(props) => (!props.isError ? '1px solid #EEEEEE' : '1px solid red')};
  text-align: center;
  border-radius: 4px;
  color: ${(props) => (props.textColor ? props.textColor : '#444444')};

  ::placeholder {
    color: ${(props) => (props.textColor ? props.textColor : '#444444')};
  }
`;

const ErrorText = styled(Text)`
  color: red;
  font-size: 12px;
  margin-top: 4px;
  margin-left: ${(props) => props.containerWidthForSetRowValueContainer && props.containerWidthForSetRowValueContainer + 16}px;
`;

const CheckboxContainer = styled('div')`
  display: flex;
  align-items: center;
`;

const CheckboxLabel = styled('label')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: #444444;
  cursor: pointer;
  user-select: none;
  margin-bottom: 0;
`;

const BreadcrumbContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  margin: 0;
  border-bottom: 1px solid #EEEEEE;
  gap: 8px;

  @media (max-width: 768px) {
    padding: 0px 12px;
    padding-bottom: 12px;
    gap: 4px;
  }
`;

const BreadcrumbStep = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const BreadcrumbNumber = styled('div')`
  min-width: 44px;
  min-height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Nunito Sans';
  font-size: 18px;
  font-weight: 600;
  background: ${(props) => (props.completed || props.active ? '#0DCC8A' : 'white')};
  color: ${(props) => (props.completed || props.active ? 'white' : '#444444')};
  border: ${(props) => {
    if (props.completed || props.active) return 'none';
    return '2px solid #757575';
  }};
  transition: all 0.2s ease;

  @media (max-width: 768px) {
    min-width: 36px;
    min-height: 36px;
    font-size: 14px;
  }
`;

const BreadcrumbText = styled('span')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: ${(props) => (props.active || props.completed ? '600' : '400')};
  color: #444444;
  transition: all 0.2s ease;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const BreadcrumbArrow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 4px;
  transform: rotate(-90deg);
`;

const ExercisePickerContainer = styled('div')`
  margin-bottom: 16px;
  margin-top: 24px;
`;

const TextColumn = styled('div')`
  display: flex;
  width: 100%;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`;

const SubheaderText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 700;
  color: #444444;
  margin-top: 24px;
  margin-bottom: 8px;
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};
  display: flex;
  align-items: center;
  gap: 8px;
  user-select: none;
  width: 100%;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: #E0E0E0;
  }

  &::before {
    margin-right: 12px;
  }

  &::after {
    margin-left: 4px;
  }
`;

const CheckboxGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px 24px;
  padding: 16px 0;
`;

const CollapsibleContainer = styled('div')`
  max-height: ${(props) => (props.isOpen ? '1000px' : '0px')};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`;

const CaretIcon = styled('div')`
  display: flex;
  align-items: center;
  transition: transform 0.3s ease-in-out;
  transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const ConfirmationText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  color: #444444;
  line-height: 1.6;
  text-align: center;
  padding: 24px 0;
`;

const HighlightText = styled('span')`
  font-weight: 700;
  color: #444444;
`;

const AdditionalOptionsText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: #444444;
  text-align: center;
`;

const SelectContainer = styled('div')`
  margin-bottom: 16px;

  .react-select__control {
    border: 1px solid #EEEEEE;
    border-radius: 4px;
    min-height: 40px;
    box-shadow: none;

    &:hover {
      border-color: #444444;
    }
  }

  .react-select__control--is-focused {
    border-color: #444444;
    box-shadow: none;
  }

  .react-select__indicator-separator {
    display: none;
  }

  .react-select__dropdown-indicator {
    color: #757575;
  }

  .react-select__menu {
    border: 1px solid #EEEEEE;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .react-select__option {
    cursor: pointer;
  }

  .react-select__option--is-focused {
    background-color: #F5F5F5;
  }

  .react-select__option--is-selected {
    background-color: #444444;
    color: white;
  }
`;

const UpdateFutureOccurrencesModal = ({ closeModal, modalData }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.data.currentUser);

  // Exercise data for picker
  const allExercises = useSelector((state) => state.calendar.data.allExercises || []);
  const tagsForAccount = useSelector((state) => state.calendar.data.tagsForAccount || []);

  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const futureOccurrencesCount = useSelector((state) => state.calendar.data.futureOccurrencesCount);

  const [containerWidthForSetRowValueContainer, setContainerWidthForSetRowValueContainer] = useState(0);
  const [setNumberValue, setSetNumberValue] = useState(1);
  const [error, setError] = useState(null); // Track error message
  const [tempo, setTempo] = useState(null);
  const [loadInputValue, setLoadInputValue] = useState(''); // Track the raw load input string
  const [repsInputValue, setRepsInputValue] = useState(''); // Track the raw reps input string

  const [rest, setRest] = useState(null);
  const [velocity, setVelocity] = useState(false);

  const [isTime, setIsTime] = useState(modalData?.content?.repType === 'time');
  const [isBodyweight, setIsBodyweight] = useState(!!modalData?.content?.repsOnly);

  const [peakPowerEnum, setPeakPowerEnum] = useState(1);
  const [percentPR, setPercentPR] = useState('');

  // Additional options checkboxes
  const [trackVolumeLoad, setTrackVolumeLoad] = useState(modalData?.content?.trackVolumeLoad || false);
  const [trackRepCount, setTrackRepCount] = useState(modalData?.content?.trackRepCount || false);
  const [forcePRUpdate, setForcePRUpdate] = useState(modalData?.content?.forceMaxUpdate || false);
  const [disablePRUpdate, setDisablePRUpdate] = useState(modalData?.content?.maxTrackingDisabled || false);
  const [completionOnly, setCompletionOnly] = useState(modalData?.content?.completionOnly || false);
  const [coachCompletion, setCoachCompletion] = useState(modalData?.content?.coachCompletion || false);
  const [eachSide, setEachSide] = useState(modalData?.content?.eachSide || false);

  // Collapsible section state
  const [isAdditionalOptionsOpen, setIsAdditionalOptionsOpen] = useState(false);

  // Exercise picker state
  const [selectedExercise, setSelectedExercise] = useState(null);
  const textWrapperRef = useRef(null);

  const repsTooltipId = `reps-tooltip-${modalData?.content?.assignedId}`;
  const peakPowerTooltipId = `peak-power-tooltip-${modalData?.content?.assignedId}`;
  const velocityTooltipId = `velocity-tooltip-${modalData?.content?.assignedId}`;

  const [primaryRows, setPrimaryRows] = useState([
    { name: 'set' },
    { name: modalData?.content?.repType === 'time' ? 'time' : 'reps' },
    { name: modalData?.content?.repsOnly ? 'bodyweight' : 'load' },
    { name: 'rest', label: 'Rest (s)' },
    { name: 'tempo' },
    { name: 'velocity' },
    { name: 'peak power', label: 'Peak Power (w)' },
    { name: '%pr' },
  ]);

  const setPercentPRFunction = (value) => {
    setPercentPR(value);
  };

  const [primaryRowSwitch, setPrimaryRowSwitch] = useState({
    set: true,
    reps: modalData?.content?.repType !== 'time',
    time: modalData?.content?.repType === 'time',
    load: false,
    bodyweight: !!modalData?.content?.repsOnly,
    rest: !!modalData?.content?.restTime,
    tempo: !!modalData?.content?.tempo,
    velocity: !!modalData?.content?.trackBarSpeed,
    'peak power': !!modalData?.content?.trackPeakPower,
    '%pr': false,
  });

  const updateColumnName = useCallback((oldName, newName) => {
    setPrimaryRows(
      (prevRows2) => prevRows2.map(
        (row) => (row.name === oldName ? { ...row, name: newName } : row),
      ),
    );

    setPrimaryRowSwitch((prevSwitch) => ({
      ...prevSwitch,
      [oldName]: false,
      [newName]: true,
    }));
  }, []);

  // Slide management
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hideOverflow, setHideOverflow] = useState(true);
  const [isFetchingCount, setIsFetchingCount] = useState(false);

  const findCorrectUnit = (unitContent) => {
    if (unitContent?.unitAbbr) {
      if (unitContent?.unitAbbr === 'wgt') {
        // orgWeight: 1 = kg, 0 = lbs
        const weightUnit = currentUser?.userSettings?.orgWeight === 1 ? 'kg' : 'lbs';
        return `(${weightUnit})`;
      }
      return `(${unitContent?.unitAbbr})`;
    }
    return '';
  };

  const setPrimaryRowSwitchState = (newState) => {
    setPrimaryRowSwitch(newState);
  };

  // Initialize state from existing data when navigating to Slide 2
  useEffect(() => {
    if (currentSlide === 1 && modalData?.content?.sets) {
      const { sets } = modalData?.content;

      // Set count (read-only display)
      setSetNumberValue(sets.length);

      // Reps as comma-separated string
      const repsArray = sets.map((set) => set.reps);
      setRepsInputValue(repsArray.join(', '));

      // Load as comma-separated string - only for non-time exercises with load data
      if (modalData?.content?.repType !== 'time' && !modalData?.content?.repsOnly) {
        const loadArray = sets.map((set) => set.prescribedValue).filter((val) => val !== null && val !== undefined);
        if (loadArray.length > 0) {
          setLoadInputValue(loadArray.join(', '));
          setPrimaryRowSwitch((prev) => ({ ...prev, load: true }));
        }
      }

      // %PR (first set's value)
      if (sets[0]?.percent) {
        setPercentPR(sets[0].percent);
        setPrimaryRowSwitch((prev) => ({ ...prev, '%pr': true }));
      }

      // Handle bodyweight exercises
      if (modalData?.content?.repsOnly) {
        setIsBodyweight(true);
        setPrimaryRowSwitch((prev) => ({ ...prev, bodyweight: true, load: false }));
        setPrimaryRows((prevRows) => prevRows.map((row) => (row.name === 'load' ? { ...row, name: 'bodyweight' } : row)));
      }

      // Handle time-based exercises
      if (modalData?.content?.repType === 'time') {
        setIsTime(true);
        setPrimaryRowSwitch((prev) => ({ ...prev, time: true, reps: false }));
        setPrimaryRows((prevRows) => prevRows.map((row) => (row.name === 'reps' ? { ...row, name: 'time' } : row)));
      }

      // Rest and Tempo from top-level properties
      if (modalData?.content?.restTime) {
        setRest(modalData?.content?.restTime);
        setPrimaryRowSwitch((prev) => ({ ...prev, rest: true }));
      }

      if (modalData?.content?.tempo) {
        setTempo(modalData?.content?.tempo);
        setPrimaryRowSwitch((prev) => ({ ...prev, tempo: true }));
      }

      // Track settings
      if (modalData?.content?.trackBarSpeed) {
        setVelocity(true);
        setPrimaryRowSwitch((prev) => ({ ...prev, velocity: true }));
      }

      if (modalData?.content?.trackPeakPower) {
        setPeakPowerEnum(modalData?.content?.trackPeakPower);
        setPrimaryRowSwitch((prev) => ({ ...prev, 'peak power': true }));
      }
    }
  }, [currentSlide, modalData]);

  // Initialize selected exercise from modalData
  useEffect(() => {
    if (currentSlide === 1) {
      // Handle tag exercises
      if (modalData?.content?.type === 'tag' && modalData?.content?.tagId && tagsForAccount.length > 0) {
        const tag = tagsForAccount.find(
          (t) => t.id === modalData?.content?.tagId,
        );

        if (tag) {
          setSelectedExercise({
            name: modalData?.content?.exerciseName || tag.name,
            id: tag.id,
            tagId: tag.id,
            type: 'tag',
            ...tag,
          });
        } else {
          // Tag not found in list - use modalData
          setSelectedExercise({
            name: modalData?.content?.exerciseName || 'Unknown Tag',
            id: modalData?.content?.tagId,
            tagId: modalData?.content?.tagId,
            type: 'tag',
          });
        }
      } else if (modalData?.content?.exerciseId && allExercises.length > 0) {
        // Handle regular exercises
        const exercise = allExercises.find(
          (ex) => ex.id === modalData?.content?.exerciseId,
        );

        if (exercise) {
          setSelectedExercise({
            name: modalData?.content?.exerciseName || exercise.name,
            id: exercise.id,
            exerciseId: exercise.id,
            type: exercise.type,
            ...exercise,
          });
        } else {
          // Exercise not found in list - use modalData
          setSelectedExercise({
            name: modalData?.content?.exerciseName || 'Unknown Exercise',
            id: modalData?.content?.exerciseId,
            exerciseId: modalData?.content?.exerciseId,
            type: modalData?.content?.type,
          });
        }
      }
    }
  }, [currentSlide, modalData, allExercises, tagsForAccount]);

  // Occurrence mode state
  const occurrenceOptions = [
    { value: 'all', label: 'Update all future occurrences (Default)' },
    { value: 'custom', label: 'Custom date range' },
  ];
  const [occurrenceMode, setOccurrenceMode] = useState(occurrenceOptions[0]);

  // Date range state
  const [startDate, setStartDate] = useState(
    modalData?.date ? moment(modalData?.date).toDate() : new Date(),
  );
  const [startDateString, setStartDateString] = useState(
    modalData?.date ? modalData?.date : moment().format('YYYY-MM-DD'),
  );

  const [endDate, setEndDate] = useState(null);
  const [endDateString, setEndDateString] = useState('');

  // Clear end date when switching from custom to all mode
  useEffect(() => {
    if (occurrenceMode.value === 'all') {
      setEndDate(null);
      setEndDateString('');
    }
  }, [occurrenceMode]);

  // Animation setup
  const showSlide1 = currentSlide === 0;
  const showSlide2 = currentSlide === 1;
  const showSlide3 = currentSlide === 2;

  const slide1Animation = useSpring({
    opacity: showSlide1 ? 1 : 0,
    transform: showSlide1 ? 'translateX(0%)' : 'translateX(-100%)',
    config: {
      duration: 300,
      easing: easings?.easeInOutQuad,
    },
    onRest: () => {
      if (showSlide1) {
        setHideOverflow(false);
      }
    },
  });

  let slide2Transform;
  if (showSlide2) {
    slide2Transform = 'translateX(0%)';
  } else if (currentSlide < 1) {
    slide2Transform = 'translateX(100%)';
  } else {
    slide2Transform = 'translateX(-100%)';
  }

  const slide2Animation = useSpring({
    opacity: showSlide2 ? 1 : 0,
    transform: slide2Transform,
    config: {
      duration: 300,
      easing: easings?.easeInOutQuad,
    },
    onRest: () => {
      if (showSlide2) {
        setHideOverflow(false);
      }
    },
  });

  let slide3Transform;
  if (showSlide3) {
    slide3Transform = 'translateX(0%)';
  } else if (currentSlide < 2) {
    slide3Transform = 'translateX(100%)';
  } else {
    slide3Transform = 'translateX(-100%)';
  }

  const slide3Animation = useSpring({
    opacity: showSlide3 ? 1 : 0,
    transform: slide3Transform,
    config: {
      duration: 300,
      easing: easings?.easeInOutQuad,
    },
    onRest: () => {
      if (showSlide3) {
        setHideOverflow(false);
      }
    },
  });

  const exerciseIdFinder = () => {
    if (selectedExercise) {
      if (selectedExercise?.exerciseId) {
        return selectedExercise.exerciseId;
      }

      if (selectedExercise?.tagId) {
        return selectedExercise.tagId;
      }
    }
    return null;
  };

  const buildSubmissionObject = () => {
    // Validate before building
    if (error) {
      showErrorToast('Please fix validation errors before proceeding');
      return null;
    }

    // Validate reps input is not empty (circuits and saq+c can have empty reps)
    if (modalData?.content?.type === 'lift' || modalData?.content?.type === 'tag') {
      if (!repsInputValue || repsInputValue.trim() === '') {
        showErrorToast('Reps field cannot be empty');
        return null;
      }
    }

    const setCount = setNumberValue;
    const hasOriginalSets = modalData?.content?.sets && modalData?.content?.sets.length > 0;
    const hasRepsInput = repsInputValue && repsInputValue.trim() !== '';
    const isSetOption = modalData?.content?.type === 'lift'
      || modalData?.content?.type === 'tag'
      || (modalData?.content?.type === 'saq+c' && hasOriginalSets && hasRepsInput)
      || (modalData?.content?.type === 'circuit' && hasOriginalSets && hasRepsInput);

    // Only build sets for lift/tag types
    let sets = [];
    if (isSetOption) {
      // Parse reps
      const repsArray = repsInputValue
        .split(',')
        .map((r) => r.trim())
        .filter(Boolean);

      // Normalize reps if single value
      const normalizedReps = repsArray.length === 1
        ? Array(setCount).fill(repsArray[0])
        : repsArray;

      // Validate count
      if (normalizedReps.length !== setCount) {
        showErrorToast(`Expected ${setCount} reps values but received ${repsArray.length}`);
        return null;
      }

      // Parse %PR values before building sets
      let normalizedPR = [];
      if (percentPR && primaryRowSwitch['%pr']) {
        const prArray = percentPR
          .split(',')
          .map((pr) => pr.trim())
          .filter(Boolean);

        // Single value applies to all sets
        normalizedPR = prArray.length === 1
          ? Array(setCount).fill(prArray[0])
          : prArray;
      }

      // Parse load values if present
      let normalizedLoad = [];
      if (loadInputValue && loadInputValue.trim() !== '' && primaryRowSwitch.load && !isBodyweight) {
        const loadArray = loadInputValue
          .split(',')
          .map((loadVal) => loadVal.trim())
          .filter(Boolean);

        // Single value applies to all sets
        normalizedLoad = loadArray.length === 1
          ? Array(setCount).fill(loadArray[0])
          : loadArray;
      }

      // Build sets array
      sets = Array.from({ length: setCount }, (_, i) => {
        const setData = {
          setNumber: i + 1,
          reps: normalizedReps[i],
        };

        // Add load if exists
        if (normalizedLoad.length > 0 && normalizedLoad[i]) {
          setData.prescribedValue = parseFloat(normalizedLoad[i]);
        }

        // Add %PR if exists and parsed
        if (normalizedPR.length > 0 && normalizedPR[i]) {
          setData.percent = normalizedPR[i];
        }

        return setData;
      });
    }

    // Build main submission object
    // Use selectedExercise if available (user may have changed it), otherwise fall back to modalData
    const submissionObject = {
      assignedId: modalData?.content?.assignedId,
      originalExerciseId: modalData?.content?.exerciseId || modalData?.content?.tagId,
      order: modalData?.content?.order,
      groupingLetter: modalData?.content?.groupingLetter,
      type: modalData?.content?.type,
      coachCompletion,
      completionOnly,
      eachSide,
      forceMaxUpdate: forcePRUpdate,
      maxTrackingDisabled: disablePRUpdate,
      description: modalData?.content?.description,
    };

    if (isSetOption) {
      submissionObject.sets = sets;
    }

    // Only add sets-related properties for lift/tag types
    if (isSetOption) {
      submissionObject.trackRepCount = trackRepCount;
      submissionObject.trackVolumeLoad = trackVolumeLoad;
      submissionObject.repsOnly = isBodyweight;
      submissionObject.repType = isTime ? 'time' : 'reps';
    }

    if (selectedExercise) {
      if (modalData?.content.type === 'tag') {
        submissionObject.tagId = exerciseIdFinder();
      } else {
        submissionObject.exerciseId = exerciseIdFinder();
      }
    }

    if (Object.keys(selectedReduxCalendar).length) {
      // If working from a parent calendar, use the parentId for the assignment's calendarId
      // The top-level calendarId will still use selectedReduxCalendar.id (handled in the action)
      if (modalData.isInherited && modalData.parentId) {
        submissionObject.calendarId = modalData.parentId;
      } else {
        submissionObject.calendarId = selectedReduxCalendar.id;
      }
    } else if (Object.keys(selectedReduxAthlete).length) {
      if (modalData.isInherited && modalData.parentId) {
        submissionObject.calendarId = modalData.parentId;
      } else {
        submissionObject.userId = selectedReduxAthlete.id;
      }
    }

    // Add optional fields if they have values
    if (rest && primaryRowSwitch.rest) {
      submissionObject.restTime = rest;
    }

    if (tempo && primaryRowSwitch.tempo) {
      submissionObject.tempo = tempo;
    }

    if (peakPowerEnum && primaryRowSwitch['peak power']) {
      submissionObject.trackPeakPower = peakPowerEnum;
    }

    if (velocity && primaryRowSwitch.velocity) {
      submissionObject.trackBarSpeed = velocity;
    }

    // Add date range fields
    submissionObject.startDate = startDateString;
    // Only include endDate when custom date range is selected
    if (occurrenceMode.value === 'custom' && endDateString) {
      submissionObject.endDate = endDateString;
    }

    // Add boolean flags
    submissionObject.updateExercise = !!(selectedExercise && exerciseIdFinder() !== (modalData?.content?.exerciseId || modalData?.content?.tagId));

    // Automatically detect if sets/reps/properties have changed (only for lift/tag types)
    let updateSets = false;

    if (isSetOption) {
      // Check if sets count changed
      if (sets?.length !== modalData?.content?.sets?.length) {
        updateSets = true;
      }

      // Check if any set values changed (reps, load, %pr)
      if (!updateSets && sets.length > 0) {
        for (let i = 0; i < sets.length; i += 1) {
          const newSet = sets[i];
          const originalSet = modalData?.content?.sets?.[i];

          // Check reps
          if (newSet.reps !== originalSet?.reps) {
            updateSets = true;
            break;
          }

          // Check load
          if (newSet.prescribedValue !== originalSet?.prescribedValue) {
            updateSets = true;
            break;
          }

          // Check %PR
          if (newSet.percent !== originalSet?.percent) {
            updateSets = true;
            break;
          }
        }
      }

      // Check if rest time changed
      if (!updateSets && rest !== modalData?.content?.restTime) {
        updateSets = true;
      }

      // Check if tempo changed
      if (!updateSets && tempo !== modalData?.content?.tempo) {
        updateSets = true;
      }

      // Check if peak power changed
      if (!updateSets && peakPowerEnum !== modalData?.content?.trackPeakPower) {
        updateSets = true;
      }

      // Check if velocity changed
      if (!updateSets && velocity !== modalData?.content?.trackBarSpeed) {
        updateSets = true;
      }

      // Check if rep type changed
      if (!updateSets && isTime !== (modalData?.content?.repType === 'time')) {
        updateSets = true;
      }

      // Check if bodyweight changed
      if (!updateSets && isBodyweight !== modalData?.content?.repsOnly) {
        updateSets = true;
      }

      submissionObject.updateSets = updateSets;
      submissionObject.updateAdvancedInfo = trackVolumeLoad !== modalData?.content?.trackVolumeLoad
        || trackRepCount !== modalData?.content?.trackRepCount
        || forcePRUpdate !== modalData?.content?.forceMaxUpdate
        || disablePRUpdate !== modalData?.content?.maxTrackingDisabled
        || completionOnly !== modalData?.content?.completionOnly
        || coachCompletion !== modalData?.content?.coachCompletion
        || eachSide !== modalData?.content?.eachSide;
    } else {
      // For non-lift/tag types, updateSets should be false
      submissionObject.updateSets = false;
      // Check basic property changes
      submissionObject.updateAdvancedInfo = forcePRUpdate !== modalData?.content?.forceMaxUpdate
        || disablePRUpdate !== modalData?.content?.maxTrackingDisabled
        || completionOnly !== modalData?.content?.completionOnly
        || coachCompletion !== modalData?.content?.coachCompletion
        || eachSide !== modalData?.content?.eachSide;
    }

    return submissionObject;
  };

  // Navigation handlers
  const handleNext = async () => {
    // Slide 1 validation
    if (currentSlide === 0) {
      // Validation: Start date is required
      if (!startDateString) {
        showErrorToast('Please select a start date');
        return;
      }

      // Validation: Start date cannot be before exercise date
      if (moment(startDateString).isBefore(moment(modalData?.date), 'day')) {
        showErrorToast('Start date cannot be before the exercise date');
        return;
      }
    }

    // Slide 2 validation and fetch count
    if (currentSlide === 1) {
      // Validate set count
      if (!setNumberValue || setNumberValue < 1 || setNumberValue > 10) {
        showErrorToast('Sets must be between 1 and 10');
        return;
      }

      const submissionObject = buildSubmissionObject();
      if (!submissionObject) {
        return; // Error already shown
      }

      setIsFetchingCount(true);

      await dispatch(fetchFutureOccurrencesCount(
        currentUser.accountCode,
        modalData?.content?.assignedId,
        modalData?.content?.exerciseId || modalData?.content?.tagId,
        submissionObject.startDate,
        submissionObject.endDate,
      ));

      setIsFetchingCount(false);
    }

    setHideOverflow(true);
    if (currentSlide < 2) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const setTempoFunction = (value) => {
    setTempo(value);
  };

  const handleBack = () => {
    setHideOverflow(true);
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const setRestFunction = (value) => {
    const intRest = parseInt(value, 10);
    // Prevent negative values
    if (intRest < 0) return;
    setRest(intRest);
  };

  const handleSetCountChange = (value) => {
    setSetNumberValue(value);

    // Re-validate existing reps against new set count
    if (repsInputValue && repsInputValue.trim() !== '') {
      const repsArray = repsInputValue
        .split(',')
        .map((r) => r.trim())
        .filter(Boolean);

      // Check if reps count matches new set count
      if (repsArray.length !== value && repsArray.length !== 1 && repsArray.length > 0) {
        setError('Enter a number for reps to apply to all sets, or enter comma-separated values matching the number of sets.');
      } else {
        setError(null);
      }
    }

    // Re-validate %PR if present
    if (percentPR && percentPR.trim() !== '') {
      const prArray = percentPR
        .split(',')
        .map((pr) => pr.trim())
        .filter(Boolean);

      if (prArray.length !== value && prArray.length !== 1 && prArray.length > 0) {
        setError('Enter a single %PR value to apply to all sets, or enter comma-separated values matching the number of sets.');
      }
    }

    // Re-validate existing load values against new set count
    if (loadInputValue && loadInputValue.trim() !== '') {
      const loadArray = loadInputValue
        .split(',')
        .map((loadVal) => loadVal.trim())
        .filter(Boolean);

      if (loadArray.length !== value && loadArray.length !== 1 && loadArray.length > 0) {
        setError('Enter a number for load to apply to all sets.');
      }
    }
  };

  const handlePercentPRBlur = () => {
    if (percentPR && percentPR.trim() !== '') {
      const prArray = percentPR
        .split(',')
        .map((pr) => pr.trim())
        .filter(Boolean);
      const setCount = setNumberValue;

      // Validate that all %PR values are valid whole numbers
      const invalidValues = [];
      prArray.forEach((pr, index) => {
        const isValid = /^\d+$/.test(pr);
        if (!isValid) {
          invalidValues.push({ value: pr, position: index + 1 });
        }
      });

      if (invalidValues.length > 0) {
        if (invalidValues.length === 1) {
          setError(`Invalid %PR format: "${invalidValues[0].value}" at set ${invalidValues[0].position}. Use whole numbers only.`);
        } else {
          const invalidList = invalidValues.map((iv) => `"${iv.value}" (set ${iv.position})`).join(', ');
          setError(`Invalid %PR format for values: ${invalidList}. Use whole numbers only.`);
        }
        return;
      }

      // Check if count matches set count (or is 1 to apply to all)
      if (prArray.length !== setCount && prArray.length !== 1) {
        setError(`Expected ${setCount} %PR values or 1 value (to apply to all sets), but have ${prArray.length} values.`);
      } else {
        setError(null);
      }
    }
  };

  const handleLoadInputChange = (value) => {
    setLoadInputValue(value);

    // For load, store the raw array and validate without auto-normalization
    const loadArray = value
      .split(',')
      .map((loadVal) => loadVal.trim())
      .filter(Boolean);

    // Validate format but don't auto-normalize
    const setCount = setNumberValue;
    const invalidValues = [];

    // Check each value for valid format (positive numbers, decimals allowed)
    loadArray.forEach((loadVal, index) => {
      const isValid = /^\d+(\.\d+)?$/.test(loadVal); // Positive numbers, decimals allowed
      if (!isValid) {
        invalidValues.push({ value: loadVal, position: index + 1 });
      } else if (parseFloat(loadVal) < 0) {
        invalidValues.push({ value: loadVal, position: index + 1 });
      }
    });

    // Show format errors
    if (invalidValues.length > 0) {
      if (invalidValues.length === 1) {
        setError(`Invalid load format: "${invalidValues[0].value}" at position ${invalidValues[0].position}. Use positive numbers only.`);
      } else {
        const invalidList = invalidValues.map((iv) => `"${iv.value}" (pos ${iv.position})`).join(', ');
        setError(`Invalid load format for values: ${invalidList}. Use positive numbers only.`);
      }
      return;
    }

    // Show count mismatch errors (but don't auto-normalize)
    if (loadArray.length !== setCount && loadArray.length !== 1 && loadArray.length > 0) {
      setError('Enter a number for load to apply to all sets.');
    } else {
      setError(null); // Clear error if valid
    }
  };

  const handleLoadBlur = () => {
    if (loadInputValue && loadInputValue.trim() !== '') {
      const loadArray = loadInputValue
        .split(',')
        .map((loadVal) => loadVal.trim())
        .filter(Boolean);
      const setCount = setNumberValue;

      // Check if all load values are valid and collect invalid ones
      const invalidValues = [];
      loadArray.forEach((loadVal, index) => {
        const isValid = /^\d+(\.\d+)?$/.test(loadVal) && parseFloat(loadVal) >= 0;
        if (!isValid) {
          invalidValues.push({ value: loadVal, position: index + 1 });
        }
      });

      // If any load is invalid, show specific error
      if (invalidValues.length > 0) {
        if (invalidValues.length === 1) {
          setError(`Invalid load format: "${invalidValues[0].value}" at set ${invalidValues[0].position}. Use positive numbers only.`);
        } else {
          const invalidList = invalidValues.map((iv) => `"${iv.value}" (set ${iv.position})`).join(', ');
          setError(`Invalid load format for values: ${invalidList}. Use positive numbers only.`);
        }
        return;
      }

      // Normalize single values to fill all sets on blur
      if (loadArray.length === 1) {
        const normalizedLoad = Array(setCount).fill(loadArray[0]);
        setLoadInputValue(normalizedLoad.join(', '));
        setError(null);
      } else if (loadArray.length === setCount) {
        setError(null); // Clear error if valid
      } else {
        setError(`Expected ${setCount} load values or 1 value (to apply to all sets), but have ${loadArray.length} values.`);
      }
    } else {
      // Clear error if field is empty
      setError(null);
    }
  };

  const handleRepsInputChange = (value) => {
    setRepsInputValue(value);

    // Check for partial "custom" input
    const trimmedValue = value.trim().toLowerCase();
    if (['c', 'cu', 'cus', 'cust'].includes(trimmedValue)) {
      setError('If you would like to program multiple sets, please enter them as comma separated values, e.g. 12,10,8 to match the number of sets');
      return;
    }

    // For reps, store the raw array and validate without auto-normalization
    const repsArray = value
      .split(',')
      .map((rep) => rep.trim())
      .filter(Boolean);

    // Validate format but don't auto-normalize
    const setCount = setNumberValue;
    const invalidValues = [];

    // Check each value for valid format
    repsArray.forEach((rep, index) => {
      let isValid = false;

      if (/^\d+\+?$/.test(rep)) {
        // Single number, optionally followed by a plus sign
        isValid = true;
      } else if (/^\d+-\d+$/.test(rep)) {
        // Range - validate that first number is less than second number
        const [start, end] = rep.split('-').map(Number);
        isValid = start <= end;
      } else if (['AMAP'].includes(rep.toUpperCase())) {
        // AMRAP/AMAP
        isValid = true;
      }

      if (!isValid) {
        invalidValues.push({ value: rep, position: index + 1 });
      }
    });

    // Show format errors
    if (invalidValues.length > 0) {
      if (invalidValues.length === 1) {
        setError(`Invalid format: "${invalidValues[0].value}" at position ${invalidValues[0].position}. Use numbers, ranges with lower value first (e.g. 8-12), minimums (e.g. 8+), or "AMAP".`);
      } else {
        const invalidList = invalidValues.map((iv) => `"${iv.value}" (pos ${iv.position})`).join(', ');
        setError(`Invalid format for values: ${invalidList}. Use numbers, ranges with lower value first (e.g. 8-12), minimums (e.g. 8+), or "AMAP".`);
      }
      return;
    }

    // Show count mismatch errors (but don't auto-normalize)
    if (repsArray.length !== setCount && repsArray.length !== 1 && repsArray.length > 0) {
      setError('Enter a number for reps to apply to all sets.');
    } else {
      setError(null); // Clear error if valid
    }
  };

  const handleRepsKeyDown = (e) => {
    const { key, target } = e;
    const { value, selectionStart, selectionEnd } = target;

    // Handle 'a' or 'A' key press for AMAP auto-expansion
    if (key.toLowerCase() === 'a') {
      // Check if we're at the start of a new value (after comma + space, or at the beginning)
      const beforeCursor = value.substring(0, selectionStart);
      const isAtValueStart = beforeCursor === '' || beforeCursor.endsWith(', ') || beforeCursor.endsWith(',');

      if (isAtValueStart) {
        e.preventDefault();
        const beforeSelection = value.substring(0, selectionStart);
        const afterSelection = value.substring(selectionEnd);
        const newValue = `${beforeSelection}AMAP${afterSelection}`;
        const newCursorPosition = selectionStart + 4; // Position after 'AMAP'

        setRepsInputValue(newValue);
        handleRepsInputChange(newValue);

        // Set cursor position directly using requestAnimationFrame for better timing
        requestAnimationFrame(() => {
          target.setSelectionRange(newCursorPosition, newCursorPosition);
        });
      }
    }

    // Handle backspace on AMAP
    if (key === 'Backspace') {
      // Check if cursor is at the end of 'AMAP'
      const beforeCursor = value.substring(0, selectionStart);
      const afterCursor = value.substring(selectionEnd);

      // Find if there's an AMAP ending right before the cursor
      if (beforeCursor.endsWith('AMAP') && selectionStart === selectionEnd) {
        e.preventDefault();

        // Remove the entire 'AMAP' word
        const newBeforeCursor = beforeCursor.substring(0, beforeCursor.length - 4);
        const newValue = newBeforeCursor + afterCursor;
        const newCursorPosition = newBeforeCursor.length;

        setRepsInputValue(newValue);
        handleRepsInputChange(newValue);

        // Set cursor position directly using requestAnimationFrame
        requestAnimationFrame(() => {
          target.setSelectionRange(newCursorPosition, newCursorPosition);
        });
      }
    }
  };

  // Submit handler
  const handleSubmit = async () => {
    const submissionObject = buildSubmissionObject();
    if (!submissionObject) {
      return; // Error already shown
    }

    const formattedDate = moment(modalData?.date).format('YYYY-MM-DD');

    dispatch(updateFutureOccurrencesProgrammedCalendarItem(
      currentUser.accountCode,
      submissionObject,
      formattedDate,
      exerciseIdFinder(),
      modalData?.content?.type === 'tag',
      {
        onSuccess: () => {
          closeModal();
        },
        onError: () => {
          // Error toast already shown by action
        },
      },
    ));
  };

  // Handler for exercise selection changes
  const handleExerciseChange = useCallback((newExercise) => {
    if (!newExercise) {
      // Prevent clearing selection
      return;
    }

    // Update selected exercise - will be used in submission object
    setSelectedExercise(newExercise);
  }, []);

  // Breadcrumb click handler
  const handleBreadcrumbClick = (step) => {
    // Only allow clicking on completed steps or current step
    if (step <= currentSlide) {
      setHideOverflow(true);
      setCurrentSlide(step);
    }
  };

  console.log(modalData?.content);

  // Render breadcrumb navigation
  const renderBreadcrumb = () => {
    const steps = [
      { number: 1, text: 'Exercise to Update', slideIndex: 0 },
      { number: 2, text: 'Edits and Updates', slideIndex: 1 },
      { number: 3, text: 'Confirmation', slideIndex: 2 },
    ];

    return (
      <BreadcrumbContainer>
        {steps.map((step, index) => {
          const isActive = step.slideIndex === currentSlide;
          const isCompleted = step.slideIndex < currentSlide;
          const isClickable = step.slideIndex <= currentSlide;

          return (
            <React.Fragment key={step.number}>
              <BreadcrumbStep
                clickable={isClickable}
                onClick={() => handleBreadcrumbClick(step.slideIndex)}
              >
                <BreadcrumbNumber
                  active={isActive}
                  completed={isCompleted}
                >
                  {isCompleted ? (
                    <IcomoonReact
                      iconSet={iconSet}
                      icon='checkmark'
                      size={20}
                      color='white'
                    />
                  ) : (
                    step.number
                  )}
                </BreadcrumbNumber>
                <BreadcrumbText
                  active={isActive}
                  completed={isCompleted}
                >
                  {step.text}
                </BreadcrumbText>
              </BreadcrumbStep>
              {index < steps.length - 1 && (
                <BreadcrumbArrow>
                  <IcomoonReact
                    iconSet={iconSet}
                    icon='gs-arrow-down'
                    size={12}
                    color='#9E9E9E'
                  />
                </BreadcrumbArrow>
              )}
            </React.Fragment>
          );
        })}
      </BreadcrumbContainer>
    );
  };

  // Get exercise name for display
  const getExerciseName = () => {
    if (modalData?.content?.exerciseName) {
      return modalData?.content?.exerciseName;
    }
    if (modalData?.content?.exercise?.name) {
      return modalData?.content?.exercise.name;
    }
    if (modalData?.content?.title) {
      return modalData?.content?.title;
    }
    return 'Unknown Exercise';
  };

  // Handler for start date changes
  const handleStartDateChange = (date) => {
    const momentDate = moment(date);
    setStartDate(momentDate.toDate());
    setStartDateString(momentDate.format('YYYY-MM-DD'));

    // If end date exists and is before new start date, clear it
    if (endDate && moment(endDate).isBefore(momentDate, 'day')) {
      setEndDate(null);
      setEndDateString('');
    }
  };

  // Handler for end date changes
  const handleEndDateChange = (date) => {
    if (date) {
      const momentDate = moment(date);
      setEndDate(momentDate.toDate());
      setEndDateString(momentDate.format('YYYY-MM-DD'));
    } else {
      setEndDate(null);
      setEndDateString('');
    }
  };

  const rowValueDecider = (name, shouldShimmer) => {
    if (shouldShimmer) {
      return (
        <Shimmer>
          <RowValueContainer shimmer>
            <ShimmerRowValue />
          </RowValueContainer>
        </Shimmer>
      );
    }
    if (name === 'tempo') {
      return (
        <RowValueContainer>
          <RowValue
            value={tempo}
            onChange={(e) => setTempoFunction(e.target.value)}
            background='#FCFCFC'
          />
        </RowValueContainer>
      );
    }
    if (name === 'rest') {
      return (
        <RowValueContainer>
          <RowValue
            type='number'
            value={rest}
            onChange={(e) => setRestFunction(e.target.value)}
            background='#FCFCFC'
          />
        </RowValueContainer>
      );
    }
    if (name === '%pr') {
      return (
        <RowValueContainer>
          <RowValue
            type='text'
            value={percentPR}
            onChange={(e) => setPercentPRFunction(e.target.value)}
            background='#FCFCFC'
            onBlur={() => {
              handlePercentPRBlur();
            }}
          />
        </RowValueContainer>
      );
    }
    if (name === 'load' || name === 'bodyweight') {
      return (
        <RowValueContainer>
          <RowValue
            isError={name === 'load' && error && !isBodyweight}
            type='text'
            value={isBodyweight ? '' : loadInputValue}
            onChange={(e) => handleLoadInputChange(e.target.value)}
            onBlur={() => {
              handleLoadBlur();
            }}
            background='#FCFCFC'
            placeholder={name === 'bodyweight' ? '-' : null}
            textColor={name === 'bodyweight' ? '#BDBDBD' : '#444444'}
            disabled={isBodyweight}
          />
        </RowValueContainer>
      );
    }
    if (name === 'peak power') {
      if (peakPowerEnum === 1) {
        return (
          <RowValueContainer>
            <RowValue
              disabled
              placeholder='-'
              textColor='#BDBDBD'
              background='#FCFCFC'
            />
          </RowValueContainer>
        );
      }
      return (
        <RowValueContainer>
          <RowValue
            disabled
            placeholder='Left'
            textColor='#BDBDBD'
            style={{
              marginRight: 8,
            }}
            background='#FCFCFC'
          />
          <RowValue
            disabled
            placeholder='Right'
            textColor='#BDBDBD'
            style={{
              marginLeft: 8,
            }}
            background='#FCFCFC'
          />
        </RowValueContainer>
      );
    }
    if (name === 'velocity') {
      return (
        <RowValueContainer>
          <RowValue
            type='number'
            disabled
            placeholder='-'
            textColor='#BDBDBD'
          />
        </RowValueContainer>
      );
    }
    if (name === 'set') {
      return (
        <SetRowValueContainer>
          <SetRowValue
            type='number'
            value={setNumberValue}
            disabled
            style={{ color: '#BDBDBD' }}
          />
        </SetRowValueContainer>
      );
    }
    return (
      <RowValueContainer>
        <RowValue
          isError={(name === 'reps' || name === 'time') && error}
          type='text'
          background='#FCFCFC'
          value={name === 'reps' || name === 'time' ? repsInputValue : undefined}
          onChange={(e) => {
            if (name === 'reps' || name === 'time') {
              handleRepsInputChange(e.target.value);
            }
          }}
          onKeyDown={name === 'reps' || name === 'time' ? handleRepsKeyDown : null}
          data-focus-target={
              // Add focus target to the first editable field that's visible (set field takes priority)
              (name === 'reps' && primaryRowSwitch.reps && !primaryRowSwitch.set)
              || (name === 'time' && primaryRowSwitch.time && !primaryRowSwitch.set && !primaryRowSwitch.reps)
              || (name === 'load' && primaryRowSwitch.load && !primaryRowSwitch.set && !primaryRowSwitch.reps && !primaryRowSwitch.time)
                ? modalData?.content?.assignedId
                : undefined
            }
        />
      </RowValueContainer>
    );
  };

  return (
    <ModalContainer hideOverflow={hideOverflow}>
      {renderBreadcrumb()}

      {showSlide1 && (
        <animated.div style={slide1Animation}>
          <SlideContent>
            {/* Section 1: Exercise to Update */}
            <ContentSection style={{
              marginTop: 24,
            }}
            >
              <SectionTitle>Exercise to Update</SectionTitle>
              <ExerciseNameDisplay>
                {getExerciseName()}
              </ExerciseNameDisplay>
            </ContentSection>

            {/* Section 2: Occurrence Selection */}
            <ContentSection>
              <SectionTitle>Occurrences</SectionTitle>

              <SelectContainer>
                <Select
                  options={occurrenceOptions}
                  value={occurrenceMode}
                  onChange={setOccurrenceMode}
                  className='react-select'
                  classNamePrefix='react-select'
                  isSearchable={false}
                />
              </SelectContainer>

              {/* Show date pickers only when custom date range is selected */}
              {occurrenceMode.value === 'custom' && (
                <DatePickerRow>
                  {/* Start Date Picker */}
                  <DatePickerWrapper>
                    <DateLabel>Start Date</DateLabel>
                    <TBDatePicker
                      activeDate={startDateString}
                      setActiveDate={handleStartDateChange}
                      additionalStyles={{
                        margin: '0',
                        maxWidth: '100%',
                      }}
                      showDropShadow={false}
                      showIcon
                      minDate={moment(modalData?.date).toDate()}
                      maxDate={null}
                    />
                  </DatePickerWrapper>

                  {/* End Date Picker */}
                  <DatePickerWrapper>
                    <DateLabel>End Date</DateLabel>
                    <TBDatePicker
                      activeDate={endDateString}
                      setActiveDate={handleEndDateChange}
                      additionalStyles={{
                        margin: '0',
                        maxWidth: '100%',
                      }}
                      showDropShadow={false}
                      showIcon
                      minDate={startDate ? moment(startDate).add(1, 'day').toDate() : moment(modalData?.date).add(1, 'day').toDate()}
                      maxDate={null}
                    />
                  </DatePickerWrapper>
                </DatePickerRow>
              )}
            </ContentSection>

            {/* Button Container */}
            <ButtonsContainer>
              <PrimaryButton
                onClick={handleNext}
                disabled={occurrenceMode.value === 'custom' && !endDateString}
              >
                NEXT
              </PrimaryButton>
              <SecondaryButton onClick={closeModal}>CANCEL</SecondaryButton>
            </ButtonsContainer>
          </SlideContent>
        </animated.div>
      )}
      {showSlide2 && (
        <animated.div style={slide2Animation}>
          {/* Exercise Picker Section */}
          <ExercisePickerContainer>
            <SectionTitle>Exercise</SectionTitle>
            <TextColumn ref={textWrapperRef}>
              <SelectDropdownExercisePickerLocal
                listItems={modalData?.content?.type === 'tag' ? tagsForAccount : allExercises}
                textWrapperRef={textWrapperRef}
                defaultValue={selectedExercise}
                selectedOption={selectedExercise}
                onExerciseChange={handleExerciseChange}
                fullWidthInput
                typeToFilter={modalData?.content?.exercise?.type}
                isTags={modalData?.content?.type === 'tag'}
                placeholder='Select exercise...'
              />
            </TextColumn>
          </ExercisePickerContainer>
          {/* Existing table content */}
          {modalData?.content?.type === 'lift' || modalData?.content?.type === 'tag' ? (
            <WrapperRow>
              <Container>
                <PrimaryRow>
                  {primaryRows.map((primaryRow) => (
                    primaryRowSwitch[primaryRow.name] ? (
                      primaryRow.name === 'set' ? <SetPrimaryValueContainer>{`${capitalizeWords(primaryRow.name)}s`}</SetPrimaryValueContainer> : (
                        <PrimaryValueContainer>
                          <PrimaryRowValue>
                            {primaryRow.name === 'reps' || primaryRow.name === 'velocity' || primaryRow.name === 'peak power' ? (
                              <NameAndDropdown>
                                {(primaryRow.name === 'peak power' || primaryRow.name === 'reps' || primaryRow.name === 'velocity') && (
                                <InfoIconWrapper>
                                  <IcomoonReact
                                    iconSet={iconSet}
                                    size={14}
                                    icon='info2'
                                    color='rgba(189, 189, 189, 1)'
                                    style={{ cursor: 'pointer' }}
                                    data-tooltip-id={
                                      primaryRow.name === 'reps' ? repsTooltipId
                                        : primaryRow.name === 'peak power' ? peakPowerTooltipId
                                          : primaryRow.name === 'velocity' ? velocityTooltipId : undefined
                                    }
                                  />
                                </InfoIconWrapper>
                                )}

                                <SwapPropDropdown
                                  setPrimaryRowSwitch={setPrimaryRowSwitch}
                                  primaryRowSwitch={primaryRowSwitch}
                                  rowName={primaryRow.name}
                                  size={12}
                                  updateColumnName={updateColumnName}
                                  name={primaryRow.name === 'load' ? capitalizeWords('Load') : primaryRow.label || capitalizeWords(primaryRow.name)}
                                  unit={primaryRow.name === 'load' ? findCorrectUnit(modalData?.content) : null}
                                  peakPowerEnum={peakPowerEnum}
                                  setPeakPowerEnum={setPeakPowerEnum}
                                  setIsBodyweight={setIsBodyweight}
                                  setIsTime={setIsTime}
                                />
                              </NameAndDropdown>
                            ) : (
                              <NameAndDropdown>
                                <SwapPropDropdown
                                  setPrimaryRowSwitch={setPrimaryRowSwitch}
                                  primaryRowSwitch={primaryRowSwitch}
                                  rowName={primaryRow.name}
                                  size={12}
                                  updateColumnName={updateColumnName}
                                  name={primaryRow.name === 'load' ? capitalizeWords('Load') : primaryRow.label || capitalizeWords(primaryRow.name)}
                                  unit={primaryRow.name === 'load' ? findCorrectUnit(modalData?.content) : null}
                                  peakPowerEnum={peakPowerEnum}
                                  setPeakPowerEnum={setPeakPowerEnum}
                                  setIsBodyweight={setIsBodyweight}
                                  setIsTime={setIsTime}
                                />
                              </NameAndDropdown>
                            )}
                          </PrimaryRowValue>
                        </PrimaryValueContainer>
                      )
                    ) : null
                  ))}
                </PrimaryRow>
                <ValueRow>
                  <ErrorAndRowsContainer>
                    <RowsContainer>
                      {primaryRows.map((primaryRow) => (
                        primaryRowSwitch[primaryRow.name] ? (
                          primaryRow.name === 'set' ? (
                            <SetRowValueContainer>
                              <ResizeObserver
                                onResize={(rect) => {
                                  setContainerWidthForSetRowValueContainer(rect?.width);
                                }}
                              />
                              <SetRowValue
                                data-focus-target={primaryRowSwitch.set ? modalData?.content?.assignedId : undefined}
                                type='number'
                                value={setNumberValue}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value, 10);
                                  // Prevent negative values and enforce max 10 sets
                                  if (value < 0) return;
                                  if (value > 10) return;
                                  handleSetCountChange(value);
                                }}
                              />
                            </SetRowValueContainer>
                          ) : rowValueDecider(primaryRow.name, false)
                        ) : null
                      ))}
                    </RowsContainer>
                    {error && <ErrorText containerWidthForSetRowValueContainer={containerWidthForSetRowValueContainer}>{error}</ErrorText>}
                  </ErrorAndRowsContainer>
                </ValueRow>
              </Container>
              <StaticDropdownWrapper>
                <StaticAddColumnDropdownSimple
                  primaryRows={primaryRows}
                  primaryRowSwitch={primaryRowSwitch}
                  setPrimaryRowSwitch={setPrimaryRowSwitch}
                  setPrimaryRowSwitchState={setPrimaryRowSwitchState}
                  size={12}
                />
              </StaticDropdownWrapper>
            </WrapperRow>
          ) : null}
          {/* Additional Options Section */}
          {modalData?.content?.type === 'lift' || modalData?.content?.type === 'tag' ? (
            <>
              <SubheaderText
                clickable
                onClick={() => setIsAdditionalOptionsOpen(!isAdditionalOptionsOpen)}
              >
                <AdditionalOptionsText>Additional Options</AdditionalOptionsText>
                <CaretIcon isOpen={isAdditionalOptionsOpen}>
                  <IcomoonReact
                    iconSet={iconSet}
                    size={12}
                    icon='gs-arrow-down'
                    color='#444444'
                  />
                </CaretIcon>
              </SubheaderText>

              <CollapsibleContainer isOpen={isAdditionalOptionsOpen}>
                <CheckboxGrid>
                  {/* Track Volume Load */}
                  <CheckboxContainer>
                    <ThemeProvider theme={checkboxTheme}>
                      <StyledCheckbox
                        checked={trackVolumeLoad}
                        onChange={(e) => setTrackVolumeLoad(e.target.checked)}
                        icon={<CustomUncheckedIcon />}
                        checkedIcon={<CustomCheckedIcon />}
                        color='default'
                        id='track-volume-load-checkbox'
                      />
                    </ThemeProvider>
                    <CheckboxLabel htmlFor='track-volume-load-checkbox'>
                      Track Volume Load
                    </CheckboxLabel>
                  </CheckboxContainer>

                  {/* Track Rep Count */}
                  <CheckboxContainer>
                    <ThemeProvider theme={checkboxTheme}>
                      <StyledCheckbox
                        checked={trackRepCount}
                        onChange={(e) => setTrackRepCount(e.target.checked)}
                        icon={<CustomUncheckedIcon />}
                        checkedIcon={<CustomCheckedIcon />}
                        color='default'
                        id='track-rep-count-checkbox'
                      />
                    </ThemeProvider>
                    <CheckboxLabel htmlFor='track-rep-count-checkbox'>
                      Track Rep Count
                    </CheckboxLabel>
                  </CheckboxContainer>

                  {/* Force PR Update */}
                  <CheckboxContainer>
                    <ThemeProvider theme={checkboxTheme}>
                      <StyledCheckbox
                        checked={forcePRUpdate}
                        onChange={(e) => setForcePRUpdate(e.target.checked)}
                        icon={<CustomUncheckedIcon />}
                        checkedIcon={<CustomCheckedIcon />}
                        color='default'
                        id='force-pr-update-checkbox'
                      />
                    </ThemeProvider>
                    <CheckboxLabel htmlFor='force-pr-update-checkbox'>
                      Force PR Update
                    </CheckboxLabel>
                  </CheckboxContainer>

                  {/* Disable PR Update */}
                  <CheckboxContainer>
                    <ThemeProvider theme={checkboxTheme}>
                      <StyledCheckbox
                        checked={disablePRUpdate}
                        onChange={(e) => setDisablePRUpdate(e.target.checked)}
                        icon={<CustomUncheckedIcon />}
                        checkedIcon={<CustomCheckedIcon />}
                        color='default'
                        id='disable-pr-update-checkbox'
                      />
                    </ThemeProvider>
                    <CheckboxLabel htmlFor='disable-pr-update-checkbox'>
                      Disable PR Update
                    </CheckboxLabel>
                  </CheckboxContainer>

                  {/* Completion Only */}
                  <CheckboxContainer>
                    <ThemeProvider theme={checkboxTheme}>
                      <StyledCheckbox
                        checked={completionOnly}
                        onChange={(e) => setCompletionOnly(e.target.checked)}
                        icon={<CustomUncheckedIcon />}
                        checkedIcon={<CustomCheckedIcon />}
                        color='default'
                        id='completion-only-checkbox'
                      />
                    </ThemeProvider>
                    <CheckboxLabel htmlFor='completion-only-checkbox'>
                      Completion Only
                    </CheckboxLabel>
                  </CheckboxContainer>

                  {/* Coach Completion */}
                  <CheckboxContainer>
                    <ThemeProvider theme={checkboxTheme}>
                      <StyledCheckbox
                        checked={coachCompletion}
                        onChange={(e) => setCoachCompletion(e.target.checked)}
                        icon={<CustomUncheckedIcon />}
                        checkedIcon={<CustomCheckedIcon />}
                        color='default'
                        id='coach-completion-checkbox'
                      />
                    </ThemeProvider>
                    <CheckboxLabel htmlFor='coach-completion-checkbox'>
                      Coach Completion
                    </CheckboxLabel>
                  </CheckboxContainer>

                  {/* Each Side */}
                  <CheckboxContainer>
                    <ThemeProvider theme={checkboxTheme}>
                      <StyledCheckbox
                        checked={eachSide}
                        onChange={(e) => setEachSide(e.target.checked)}
                        icon={<CustomUncheckedIcon />}
                        checkedIcon={<CustomCheckedIcon />}
                        color='default'
                        id='each-side-checkbox'
                      />
                    </ThemeProvider>
                    <CheckboxLabel htmlFor='each-side-checkbox'>
                      Each Side
                    </CheckboxLabel>
                  </CheckboxContainer>
                </CheckboxGrid>
              </CollapsibleContainer>
            </>
          ) : null}
          <ButtonsContainer>
            <PrimaryButton onClick={handleNext} disabled={isFetchingCount}>
              {isFetchingCount ? 'LOADING...' : 'NEXT'}
            </PrimaryButton>
            <SecondaryButton onClick={handleBack}>BACK</SecondaryButton>
          </ButtonsContainer>
        </animated.div>
      )}
      {showSlide3 && (
        <animated.div style={slide3Animation}>
          <SlideContent>
            <ConfirmationText>
              {futureOccurrencesCount !== null ? (
                <>
                  You are about to change
                  {' '}
                  <HighlightText>
                    {futureOccurrencesCount}
                    {' '}
                    instances
                  </HighlightText>
                  {' '}
                  of
                  {' '}
                  <HighlightText>{modalData?.content?.title || 'the exercise'}</HighlightText>
                  {selectedExercise && selectedExercise.exerciseId !== modalData?.content?.exerciseId && (
                    <>
                      {' '}
                      to
                      {' '}
                      <HighlightText>{selectedExercise.name}</HighlightText>
                    </>
                  )}
                </>
              ) : (
                <>
                  You are about to update future occurrences of
                  {' '}
                  <HighlightText>{modalData?.content?.title || 'the exercise'}</HighlightText>
                  {selectedExercise && selectedExercise.exerciseId !== modalData?.content?.exerciseId && (
                    <>
                      {' '}
                      to
                      {' '}
                      <HighlightText>{selectedExercise.name}</HighlightText>
                    </>
                  )}
                </>
              )}
            </ConfirmationText>
            <ButtonsContainer>
              <PrimaryButton onClick={handleSubmit}>CONFIRM</PrimaryButton>
              <SecondaryButton onClick={handleBack}>BACK</SecondaryButton>
            </ButtonsContainer>
          </SlideContent>
        </animated.div>
      )}
    </ModalContainer>
  );
};

UpdateFutureOccurrencesModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  modalData: PropTypes.instanceOf(Object).isRequired,
};

export default UpdateFutureOccurrencesModal;
